import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';

interface Message {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
}

interface ChatbotProps {
    isOpen: boolean;
    onClose: () => void;
}

// Comprehensive Knowledge Base based on user document
const knowledgeBase = {
    bio: "I’m Musab Digale, a Software Developer and AI Automation Engineer specializing in Technology & Business (TechBusiness). I build scalable digital systems that combine clean design, strong architecture, and intelligent automation to solve real business problems. I'm the Co-Founder of Digale Advert (est. 2020) and currently preparing to launch Center Store.",
    services: [
        "• Website Development (Business, Portfolio, Platforms)",
        "• SaaS Development (Web apps with secure backends)",
        "• UI/UX Design (Modern interfaces in Figma)",
        "• System Design (Database structuring & architecture)",
        "• AI Automation (Workflow efficiency)",
        "• AI Agents (Productivity & customer interaction)"
    ],
    skills: {
        frontend: "React, Next.js, Tailwind CSS",
        backend: "Nodejs, NestJs, Mysql, Supabase, API Integration",
        design: "Figma, UX Research, Wireframing",
        data: "Data cleaning, dashboards, reporting insights",
        ai: "GPT integrations, AI workflows, automation systems"
    },
    contact: {
        phone: "+252 610 437 466",
        email: "muscabdigale@gmail.com",
        location: "Mogadishu, Somalia",
        whatsapp: "+252 610 437 466"
    },
    faqs: {
        projects: "I’ve built portfolio websites, business websites, management systems, SaaS platforms, and AI automation projects — including AI agents and intelligent workflows.",
        custom: "Yes! If you have a business idea, I can turn it into a fully working digital product with a clear strategy and professional execution.",
        remote: "Yes, I work as a freelancer and handle remote projects worldwide."
    }
};

const quickReplies = [
    "Who is Musab?",
    "Expertise & Skills",
    "Services offered",
    "Contact Info",
    "Remote work?"
];

const Chatbot: React.FC<ChatbotProps> = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            text: "Salaan! 👋 I'm Musab's AI assistant. I can tell you about his skills, services, or how to start a project. Maxaan kuu qaban karaa?",
            sender: 'bot',
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    const handleSendMessage = (text: string) => {
        if (!text.trim()) return;

        const newUserMessage: Message = {
            id: Date.now().toString(),
            text: text,
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, newUserMessage]);
        setInputValue('');
        setIsTyping(true);

        // Advanced keyword matching for English and Somali
        setTimeout(() => {
            const lowerInput = text.toLowerCase();
            let responseText = "Si sax ah uma mawaan fahmin su'aashaada. Ma rabtaa inaad ka ogaato Skills-ka Musab, Services-ka, mise Contact-kiisa?\n\n(I didn't quite catch that. Would you like to know about Musab's Skills, Services, or Contact info?)";

            // About / Bio
            if (lowerInput.match(/who|musab|bio|kuma|tahay|waa kuma|yahay|saaxib/)) {
                responseText = knowledgeBase.bio;
            }
            // Services / What he does
            else if (lowerInput.match(/service|offer|do|qabataa|maxaad|u qabataa|shaqo|adeeg/)) {
                responseText = "Musab offers these professional services:\n" + knowledgeBase.services.join('\n');
            }
            // Skills
            else if (lowerInput.match(/skill|tech|stack|backend|frontend|luuqadaha|cilmi/)) {
                responseText = `Musab's technical expertise includes:\n\n• Frontend: ${knowledgeBase.skills.frontend}\n• Backend: ${knowledgeBase.skills.backend}\n• AI/Automation: ${knowledgeBase.skills.ai}\n• Design: ${knowledgeBase.skills.design}`;
            }
            // Contact / Location
            else if (lowerInput.match(/contact|email|phone|whatsapp|call|xiriir|number|lambarka|halkeed|joogtaa|location/)) {
                responseText = `You can reach Musab here:\n\n📍 Location: ${knowledgeBase.contact.location}\n📞 Phone/WA: ${knowledgeBase.contact.phone}\n📧 Email: ${knowledgeBase.contact.email}\n\nHe is currently based in Mogadishu but works worldwide.`;
            }
            // Projects
            else if (lowerInput.match(/project|built|mashruuc|maxaad dhigtay/)) {
                responseText = knowledgeBase.faqs.projects;
            }
            // Custom builds / Help
            else if (lowerInput.match(/build|help|start|caawin|ma i caawin|wax ma dhis/)) {
                responseText = knowledgeBase.faqs.custom;
            }
            // Remote
            else if (lowerInput.match(/remote|freelance|farsamo/)) {
                responseText = knowledgeBase.faqs.remote;
            }
            // Greetings
            else if (lowerInput.match(/hello|hi|hey|salaan|is ka warran|noolow/)) {
                responseText = "Salaan! 👋 How can I assist you with Musab's portfolio or projects today?";
            }

            const botResponse: Message = {
                id: (Date.now() + 1).toString(),
                text: responseText,
                sender: 'bot',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, botResponse]);
            setIsTyping(false);
        }, 1200);
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end pointer-events-none">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="mb-4 w-[320px] sm:w-[380px] pointer-events-auto"
                    >
                        <Card className="border-neon/20 shadow-2xl overflow-hidden backdrop-blur-md bg-card/95 h-[500px] flex flex-col rounded-[2rem]">
                            <CardHeader className="bg-neon p-5 flex flex-row items-center justify-between space-y-0">
                                <div className="flex items-center gap-3">
                                    <div className="bg-black/10 p-2 rounded-xl backdrop-blur-sm">
                                        <Bot className="w-5 h-5 text-black" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-base font-bold text-black">Musab AI</CardTitle>
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                                            <span className="text-xs font-semibold text-black/70 italic">Kala soco xogtiisa halkan</span>
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={onClose}
                                    className="hover:bg-black/10 text-black h-8 w-8 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </CardHeader>

                            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-neon/20 scrollbar-track-transparent">
                                {messages.map((message) => (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        key={message.id}
                                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'
                                            }`}
                                    >
                                        <div
                                            className={`max-w-[85%] px-4 py-3 text-sm shadow-sm whitespace-pre-wrap ${message.sender === 'user'
                                                ? 'bg-neon text-black rounded-2xl rounded-tr-sm font-bold shadow-neon/10'
                                                : 'bg-secondary/50 text-foreground rounded-2xl rounded-tl-sm border border-border/50 backdrop-blur-sm'
                                                }`}
                                        >
                                            {message.text}
                                        </div>
                                    </motion.div>
                                ))}
                                {isTyping && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="flex justify-start"
                                    >
                                        <div className="bg-secondary/30 px-4 py-3 rounded-2xl rounded-tl-sm border border-border/50 flex gap-1 items-center h-[36px]">
                                            <motion.span
                                                animate={{ y: [0, -4, 0] }}
                                                transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                                                className="w-1 h-1 bg-foreground/40 rounded-full"
                                            />
                                            <motion.span
                                                animate={{ y: [0, -4, 0] }}
                                                transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                                                className="w-1 h-1 bg-foreground/40 rounded-full"
                                            />
                                            <motion.span
                                                animate={{ y: [0, -4, 0] }}
                                                transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                                                className="w-1 h-1 bg-foreground/40 rounded-full"
                                            />
                                        </div>
                                    </motion.div>
                                )}
                                <div ref={messagesEndRef} />
                            </CardContent>

                            <div className="px-4 pb-3">
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
                                    {quickReplies.map((reply, i) => (
                                        <motion.button
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.1 }}
                                            key={reply}
                                            onClick={() => handleSendMessage(reply)}
                                            className="whitespace-nowrap px-4 py-2 text-xs font-bold rounded-xl bg-secondary/80 hover:bg-neon hover:text-black transition-all border border-border/50 hover:border-neon shadow-sm"
                                        >
                                            {reply}
                                        </motion.button>
                                    ))}
                                </div>
                            </div>

                            <CardFooter className="p-4 bg-secondary/20 border-t border-border/20 backdrop-blur-sm">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleSendMessage(inputValue);
                                    }}
                                    className="flex w-full gap-2 relative"
                                >
                                    <Input
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        placeholder="Qor su'aal..."
                                        className="flex-1 bg-background/50 border-border/50 focus-visible:ring-neon/50 pr-12 rounded-xl h-12"
                                    />
                                    <Button
                                        type="submit"
                                        size="icon"
                                        className="absolute right-1 top-1 h-10 w-10 bg-neon text-black hover:bg-neon/80 transition-transform active:scale-95 rounded-lg"
                                        disabled={!inputValue.trim()}
                                    >
                                        <Send className="w-5 h-5" />
                                    </Button>
                                </form>
                            </CardFooter>
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Chatbot;
